import * as angular from 'angular';
import * as _ from 'lodash';

import { KeyValuePair, SelectorType, IdentifierType, ComponentDefinition } from '../../models/index';
import { IidentifierService, ILoggerService } from '../../services/index';
import { tagDefinitionValues, reconciledValues, reconciledConstraints, identifierValues, validatedValues, targetValues } from '../../common/constants';
import { ValueTypes } from '../../common/ValueTypes';

class ValueSelectionController implements angular.IComponentController {
    properties: KeyValuePair<string, string>[];
    seriesType: SelectorType;
    seriesValue: string;

    readonly allowRawValues: boolean;
    readonly hasComment: boolean;
    readonly hasSelection: boolean;
    readonly identifierGuid: string;
    readonly identifierType: IdentifierType;
    readonly isDisabled: boolean;
    readonly isQuery: boolean;
    readonly onChange?: () => void;
    readonly queryValueTypes: KeyValuePair<string, string>[];
    readonly seriesConstraint: boolean;
    readonly seriesReconciled: boolean;

    private _unwatch: () => void;

    static $inject = [
        '$scope',
        'identifierService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _identifierService: IidentifierService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this._unwatch = this._$scope.$watchGroup([() => this.seriesType, () => this.identifierType], () => {
            if (this.isQuery) {
                this.setQueryProperties();
            } else if (this.seriesType || this.seriesType === SelectorType.tagDefinition) {
                this.setSeriesProperties();
                if (this.seriesReconciled) {
                    this.properties = this.properties.concat(reconciledValues);
                }
                if (this.seriesConstraint) {
                    this.properties = this.properties.concat(reconciledConstraints);
                }
            }
        });

    }

    private setSeriesProperties() {
        if (this.seriesType === SelectorType.tagDefinition) {
            this.properties = this.getVariables(IdentifierType.Tag);
        } else if (this.seriesType === SelectorType.historianTag) {
            this.properties = [ValueTypes.raw];
        } else {
            if (this.identifierType === IdentifierType.Linked) {
                this.setLinkedProperties();
            } else {
                this.properties = this.getVariables(this.identifierType);
            }
        }
    }

    private setLinkedProperties() {
        return this._identifierService.getNativeIdentifierByGuid(this.identifierGuid)
            .then(identifier => {
                this.properties = this.getVariables(identifier.type);
                if (this.properties.length > 0) {
                    if (this.seriesValue) {
                        const found = _.find(this.properties, p => p.key === this.seriesValue);
                        if (!found) {
                            this.seriesValue = this.properties[0].key;
                        }
                    } else {
                        this.seriesValue = this.properties[0].key;
                    }
                }
            });
    }

    private setQueryProperties() {
        if (this.queryValueTypes?.length) {
            this.properties = this.queryValueTypes;
        } else {
            this._loggerService.logError('No value types from query available for value selection.');
        }
    }

    $onDestroy() {
        this._unwatch();
    }

    onUpdate() {
        if (this.onChange) {
            this.onChange();
        }
    }

    private getVariables(type: IdentifierType): KeyValuePair<string, string>[] {
        if (type === undefined || type === null || type === IdentifierType.CalculatedValue) {
            return identifierValues.map(d => d);
        } else if (type === IdentifierType.Tag) {
            const ret = tagDefinitionValues.map(d => d);
            if (this.allowRawValues) {
                ret.push(ValueTypes.raw);
            }
            return ret;
        } else if (type === IdentifierType.Validated) {
            const ret = validatedValues.map(d => d);
            if (this.hasComment) {
                ret.push(ValueTypes.comment);
            }
            if (this.hasSelection) {
                ret.push(ValueTypes.selection);
            }
            return ret;
        } else if (type === IdentifierType.Target) {
            return targetValues.map(d => d);
        } else {
            this._loggerService.logError('Unknown identifier type for value selection');
        }
    }
}

export const wtValueSelection: ComponentDefinition = {
    componentId: 'wtValueSelection',
    controller: ValueSelectionController,
    template: require('./valueSelection.component.html'),
    controllerAs: 'vm',
    bindings: {
        allowRawValues: '<?',
        hasComment: '<?',
        hasSelection: '=?',
        identifierGuid: '@',
        identifierType: '=',
        isDisabled: '<?',
        isQuery: '<?',
        onChange: '&?',
        queryValueTypes: '<?',
        seriesConstraint: '<',
        seriesReconciled: '<',
        seriesType: '=',
        seriesValue: '='
    }
};